import React from "react";
import _ from "lodash";

import {ProductsService} from "../../products_step_service";
import {onlineBankHelpText} from "../StepContent/components/step/ProductsSelectionSteps/components/DepotStep/table-data";
import {
  DEPOT_OPENING_ONLY_OPTION_UID,
  DEPOT_OPENING_WITH_ASSET_OPTION_UID,
  EMPLOYER_STEP_ID,
  OPTION_IS_NOT_AVAILABLE_FOR_DEPOT_MSG, PRODUCT_SELECTION_SUMMARY_STEP_ID
} from "../../constants";
import {addCustomerName, getVatCoef, splitEinzeltitelTradingInstruments} from "../../utils";
import {
  instrumentDataPath,
  SAVINGS_PLANS_KEY,
  SINGLE_INVESTMENTS_KEY
} from "../../mock_produktauswahl";
import {productsStepsDataV2} from "./mock_product_selection";
import SafeHtml from "../../../../components/SafeHtml";
import {
  calculateServiceFeeVAT, getSummaryAvailableOptions, getSummaryDiscountsOptions, getSummaryServiceFeeMax,
  SUMMARY_COMMON_MAX_SERVICE_FEE, SUMMARY_DEPOT_DISCOUNT_INFO
} from "../StepContent/components/step/ProductsSelectionSteps/components/SummaryStep/utils";

const setInstrumentDiscountAndFee = (discountsAnswer, i, max_discount, broker_fee) => {
  const dataPath = instrumentDataPath(i);
  _.set(discountsAnswer, [...dataPath, 'max_discount'], max_discount);
  _.set(discountsAnswer, [...dataPath, 'broker_fee'], broker_fee);
}

export class ProductsServiceV2 extends ProductsService {
  constructor(uid, name, next_btn, success_text, getPrevStepAnswer, validateAccountFunc, handleWarnings) {
    super(uid, name, next_btn, success_text, getPrevStepAnswer, validateAccountFunc, handleWarnings);
    this.__stepsToValidate = ['product-selection-investment-recommendation'];
  }

  getDepot(withContractTypeData=false) {
    const custodianId = this.getStepAnswer('product-selection-depot', 'custodians');
    const depot = custodianId && _.get(this._depotsDataGrouped, custodianId);
    if (depot && withContractTypeData) {
      const depotType = this.getStepAnswer('product-selection-depot-type', 'depot-type');
      depot.selected_contract_type = depot.contract_types.find(
        contract => contract.value === depotType);
    }

    return depot;
  }

  /**
   * Builds all derived data for the Summary (Anlageplan) step and seeds question
   * metadata / default answers on the service side.
   *
   * @returns data: Object,
   */
  prepareSummaryStep(summaryStep) {
    const [discountsQuestion, kickbacksQuestion, serviceFeeQuestion, serviceFeeVatQuestion] = summaryStep.question;

    // default discounts answer used when the step has not been filled yet
    let discountsAnswer = discountsQuestion.answer;
    if (!discountsAnswer) {
      discountsAnswer = {
        discountsEnabled: false,
        overall: '',
        [SAVINGS_PLANS_KEY]: '',
        [SINGLE_INVESTMENTS_KEY]: ''
      };
    }

    const data = this.buildData();
    const modelPortfolio = data.instruments_source[0];

    data.custodian = {depot: this.getDepot(true) || {}};
    data.modelPortfolio = modelPortfolio;
    data.custodian.contract = data.custodian.depot.selected_contract_type || {};

    const bankConfig = {
      ...this.banksDataGrouped[data.custodian.depot.bankCompanyId],
      ...data.custodian.contract.configuration
    };

    const savings_plan_key = data["single_investment['checkbox']"][0]
      ? 'savings_plan'
      : data["vl_plan['checkbox']"][0] ? 'vl_plan' : undefined;
    data.isVlFlow = savings_plan_key === 'vl_plan';

    serviceFeeQuestion.optional = serviceFeeVatQuestion.optional = !bankConfig.onboarding_service_fee_required;

    // build up the discounts answer with instrument rows / limits depending on flow

    if (!modelPortfolio) {
      const bankInstrumentsInfo = this.getBankInstrumentsInfo(data.custodian.depot.bankCompanyId);

      let maxFundsInstrumentsOverallDiscount = 100;

      data.instruments[0].forEach(i => {
        const info = bankInstrumentsInfo[i.isin];
        if (info) {
          setInstrumentDiscountAndFee(discountsAnswer, i, info.c_front_end_load_max_discount, info.c_front_end_load_percent)

          if (i.is_fund) {
            const max_discount = info.c_front_end_load_max_discount || 0;
            maxFundsInstrumentsOverallDiscount = _.min([max_discount, maxFundsInstrumentsOverallDiscount]);
          }
        }
      });

      data.maxFundsInstrumentsOverallDiscount = maxFundsInstrumentsOverallDiscount;
      discountsAnswer.combinedDiscounts = bankConfig.combined_discounts;

      serviceFeeQuestion.maxValue = data.isVlFlow
        ? getSummaryServiceFeeMax(bankConfig, 'vl_service_fee_max')
        : getSummaryServiceFeeMax(bankConfig);
    } else {
      if(modelPortfolio.baseFondTransaction){
        if(modelPortfolio.baseFondTransaction.amount) {
          setInstrumentDiscountAndFee(
            discountsAnswer, modelPortfolio.baseFondTransaction, modelPortfolio.baseFondTransaction.max_discount,
            modelPortfolio.baseFondTransaction.broker_fee
          )
        }
        if(modelPortfolio.baseFondTransaction.savingsPlanAmount) {
          setInstrumentDiscountAndFee(
            discountsAnswer,
            {...modelPortfolio.baseFondTransaction, savingsPlan: true},
            modelPortfolio.baseFondTransaction.max_discount,
            modelPortfolio.baseFondTransaction.broker_fee
          )
        }
      }

      if (modelPortfolio.isInvestmentStrategy) {
        serviceFeeQuestion.question_text = 'Anteil Vertrieb p.a. (zzgl. MwSt.)';
        serviceFeeVatQuestion.question_text = 'Anteil Vertrieb p.a. (inkl. MwSt.)';
        serviceFeeQuestion.maxValue = modelPortfolio.broker_commission;

        // Extend discount data with rows for each instrument
        // to re-use existing Ex-Ante functionality for model portfolios
        data.instruments[0].forEach(i => {
          _.set(discountsAnswer, instrumentDataPath(i), {});
        });
      } else if (modelPortfolio.is_modelportfolio) {
        serviceFeeQuestion.maxValue = data.isVlFlow
          ? getSummaryServiceFeeMax(bankConfig, 'vl_service_fee_max')
          : getSummaryServiceFeeMax(bankConfig);

        // Extend discount data with rows for each instrument
        // to re-use existing Ex-Ante functionality for model portfolios
        data.instruments[0].forEach(i => {
          _.set(discountsAnswer, instrumentDataPath(i), {});
        });
      }
    }

    discountsQuestion.answer = discountsAnswer;
    discountsQuestion.config.choices = getSummaryDiscountsOptions(bankConfig, data.isVlFlow ? 'vl_discount' : 'discount');
    kickbacksQuestion.config.choices = bankConfig.kickback_allowed
      ? getSummaryAvailableOptions(bankConfig, data.isVlFlow ? 'vl_kickback' : 'kickback')
      : [];

    // group instruments for the tables
    const instrumentsGrouped = {};
    data.instruments[0].forEach(i => {
      const instr = i.savingsPlan ? {savingsPlanItem: i} : {singleInvestment: i};
      instrumentsGrouped[i.isin] = {...instrumentsGrouped[i.isin], ...instr, is_fund: i.is_fund};
    });

    data.instrumentsGrouped = Object.values(instrumentsGrouped);
    data.einzeltitelInstruments = [];
    data.fundsInstruments = [];
    splitEinzeltitelTradingInstruments(data.instrumentsGrouped, {}, data.einzeltitelInstruments, data.fundsInstruments);
    data.einzeltitelsOnly = data.einzeltitelInstruments.length > 0 && data.fundsInstruments.length === 0;
    data.infoText = bankConfig.onboarding_summary_info_text || [];
    data.entryFeeInfoText = bankConfig.entry_fee_info_text || OPTION_IS_NOT_AVAILABLE_FOR_DEPOT_MSG;
    data.depotDiscountInfoText = bankConfig.depot_discount_info_text || SUMMARY_DEPOT_DISCOUNT_INFO;
    data.depotDiscountDisableReason = bankConfig.depot_discount_disable_reason;
    data.commonMaxServiceFee = SUMMARY_COMMON_MAX_SERVICE_FEE;

    if (_.isNil(serviceFeeQuestion.answer)) {
      let defaultFee;
      if (!!modelPortfolio) {
        defaultFee = modelPortfolio.service_fee_per_year || serviceFeeQuestion.maxValue;
      }
      serviceFeeQuestion.answer = defaultFee || 0;
      serviceFeeVatQuestion.answer = calculateServiceFeeVAT(serviceFeeQuestion.answer, getVatCoef())
    }

    kickbacksQuestion.tooltip = bankConfig.kickback_info_text;
    if(kickbacksQuestion.config.choices.length === 0){
      kickbacksQuestion.answer = undefined;
    } else {
      const defaultKickback = bankConfig.default_kickback || 0;
      // set default in case no answer or prev answer is not available
      if (_.isNil(kickbacksQuestion.answer) || !_.find(kickbacksQuestion.config.choices, (o) => o.value == kickbacksQuestion.answer)) {
        kickbacksQuestion.answer = defaultKickback;
      }

      // disable dropdown in case defaultKickback is the only option
      kickbacksQuestion.disabled = kickbacksQuestion.config.choices.length === 1
        && defaultKickback == kickbacksQuestion.config.choices[0].value;
    }

    summaryStep.stepsData = data;
  }

  async syncDepotTypeInfo() {

    const custodianId = this.getStepAnswer('product-selection-depot', 'custodians');
    if (!custodianId) {
      return
    }

    try {
      await this._getBankDetails()

    } catch (error) {
      console.error(`Unable to sync depot type info.\nError: ${error}`)
    }

  }

  initCustodianChoices(depotsQuestion){
    // Einzeltitel is Offline process
    const einzeltitelEnabled = this.isEinzeltitelEnabled();
    const singleInvestEnabled = this._isSingleInvestmentEnabled();
    const isPIEnabled = this.isPIProcess();
    const isMPIEnabled = this.isMPProcess();
    const isVLEnabled = this._isVLEnabled();
    const isPBEnabled = this._isPortfolioBuilderEnabled();
    const isIndividualAssetManagementEnabled = this.isIndividualAssetManagementProcess();

    depotsQuestion.config.choices = depotsQuestion.options.map(c => {
      const bankConfig = this.banksDataGrouped[c.bankCompanyId];

      let tooltipText = undefined;
      if (!einzeltitelEnabled && c.is_online) {
        tooltipText = onlineBankHelpText()
      } else if (_.has(bankConfig, 'info_text')) {
        tooltipText = <SafeHtml html={bankConfig['info_text']} allowed_attrs={['href', 'target']} allowed_tags={['a']}/>
      }

      return {
        id: c.bankCompanyId,
        uid: c.bankCompanyId,
        text: c.depot,
        disabled: !_.get(bankConfig, 'onboarding_allowed', true),
        disabledReason: _.get(bankConfig, 'onboarding_disallowed_reason'),
        image: c.logo,
        info: tooltipText,
        contract_types: this.__getContractTypesForSelectedInvestment(
          c, singleInvestEnabled, einzeltitelEnabled, isPIEnabled, isMPIEnabled, isVLEnabled, isPBEnabled,
          isIndividualAssetManagementEnabled
        ),
        depot: c,  // depot data should be stored in db
      }
    }).filter(c => !_.isEmpty(c.contract_types));
  }

  __getContractTypesForSelectedInvestment(bank, singleInvestEnabled, einzeltitelEnabled, isPIEnabled, isMPIEnabled, isVLEnabled, isPBEnabled, isIndividualAssetManagementEnabled){
    const contractTypes = _.get(bank, 'contract_types', []);

    // for PI flow - only PI bank, PI bank only for PI flow
    if(isPIEnabled){
      return bank.is_private_investment ? [...contractTypes] : [];
    } else if(bank.is_private_investment){
      return [];
    }

    if(!_.some([singleInvestEnabled, einzeltitelEnabled, isMPIEnabled, isVLEnabled, isPBEnabled || isIndividualAssetManagementEnabled])){
      return [];
    }

    return _.filter(contractTypes, (contract) => {
      return (!einzeltitelEnabled || contract.einzeltitelEnabled) &&
        (!singleInvestEnabled || contract.singleInvestEnabled) &&
        (!isMPIEnabled || contract.isMPEnabled) &&
        (!isVLEnabled || contract.isVLEnabled) &&
        (!isPBEnabled || contract.isPBEnabled) &&
        (!isIndividualAssetManagementEnabled || contract.isIndividualAssetManagementEnabled)
    });
  }

  getDataForAccount(validate, forExAnte = false) {
    const data = super.getDataForAccount(validate, forExAnte);

    if(this._isVLEnabled()){
      // For Trading flow we have a list in this.bank, so please update get is_employer_information_mandatory flag if we add VL to Trading
      this._getStepAnswer(data, this.getStep(EMPLOYER_STEP_ID));
      if(data.employer) {
        if(this.bank && !this.bank.is_employer_information_mandatory) {
          data.employer = Object.entries(data.employer).reduce((a,[k,v]) => (!v ? a : (a[k]=v, a)), {});
          if(_.isEmpty(data.employer)) {
            delete data.employer
          }
        }
      }
    }

    return data;
  }

  removeEmployerStep() {
    this.__removeSteps([EMPLOYER_STEP_ID]);
  }

  addEmployerStep() {
    this.__addSteps([EMPLOYER_STEP_ID], 'product-selection-depot-type');
  }

  showFunds(){
    // the only case when funds table should not be shown when Einzeltitel selected without SingleInvestment
    return !this.isEinzeltitelEnabled() || this._isSingleInvestmentEnabled();
  }

  getPortfolioInvestedData(item){
    const singleBuyAmount = item.amount || 0;
    const savingsPlanAmount = item.savingsPlanAmount || 0;

    return {
      single_investment_enabled: singleBuyAmount > 0,
      single_investment: singleBuyAmount,
      savings_plan_enabled: savingsPlanAmount > 0,
      savings_plan: savingsPlanAmount,
    }
  }

  async __setQuestionnaire(onboardingAnswers) {
    await super.__setQuestionnaire(onboardingAnswers);

    if(this._isVLEnabled()){
      this._checkEmployerStepQuestions(this.getStep(EMPLOYER_STEP_ID));
    } else {
      this.removeEmployerStep()
    }

    const summaryStep = this.getStep(PRODUCT_SELECTION_SUMMARY_STEP_ID);
    if(summaryStep && !summaryStep.stepsData) {
      this.prepareSummaryStep(summaryStep)
    }
  }

  _afterStepChange(){
    if(this.step.uid === EMPLOYER_STEP_ID){
      this._checkEmployerStepQuestions(this.step);
    } else if(this.step.uid === this.__depotOpeningStepUID){
      // init depot opening options
      const depotOpeningQ = this.getQuestion(this.step.uid, 'depot-opening-type');
      const openOnlyOption = depotOpeningQ.config.choices.find(o => o.uid === DEPOT_OPENING_ONLY_OPTION_UID);
      const isPBFlow = this._isPortfolioBuilderEnabled();
      const bankConfig = this.getBankConfig();
      openOnlyOption.disabled = (
        isPBFlow ||
        this._isVLEnabled() ||
        !_.get(bankConfig, 'onboarding_open_only_allowed')
      );
      const isOpenOnly = depotOpeningQ.answer === openOnlyOption.uid;

      if(openOnlyOption.disabled && isOpenOnly){
        depotOpeningQ.answer = null;
      } else {
        // update steps
        if (isOpenOnly && !_.get(bankConfig, 'onboarding_open_only_product_selection_required')){
          this.removeInstrumentsSteps();
        } else {
          this.addInstrumentsSteps();
        }
      }
      if(isPBFlow){
        depotOpeningQ.answer = DEPOT_OPENING_WITH_ASSET_OPTION_UID;
      }
    } else if(this.step.uid === PRODUCT_SELECTION_SUMMARY_STEP_ID){
      this.prepareSummaryStep(this.step)
    }
  }

  isPIProcess(){
    return this.getStepAnswer("product-selection-deposits", "pi['checkbox']");
  }

  isIndividualAssetManagementProcess(){
    return this.getStepAnswer("product-selection-deposits", "individual_asset_management['checkbox']");
  }

  isMPProcess(){
    return this.getStepAnswer("product-selection-deposits", "mp['checkbox']");
  }

  __getSteps() {
    return {
      steps: this.__deepCopy(productsStepsDataV2).map(step => {
        if(step.withUserName){
          addCustomerName(step, this.current_customer);
        }

        // For Trading flow we have a list of banks, so please update get is_employer_information_mandatory flag if we add VL to Trading
        if(!this.is_trading && step.uid === EMPLOYER_STEP_ID){
          this._checkEmployerStepQuestions(step);
        }

        return step
      })
    }
  }
}